import { Component } from '@theme/component';
import { replayClass, scrollBehavior } from '@theme/utilities';

/*
 * <product-rail> — horizontal rails with optional tabs, arrows and a scroll progress bar.
 * Used by snippets/product-rail.liquid, sections/product-rail.liquid, sections/category-showcase.liquid,
 * sections/journal.liquid and the product recommendations rail.
 *
 * Markup contract (load the script from the Liquid file that renders the element:
 * `<script src="{{ 'product-rail.js' | asset_url }}" type="module" fetchpriority="low"></script>`):
 *
 *   <product-rail class="product-rail ...">            root (CSS: `product-rail { display: block }`
 *                                                       lives in snippets/product-rail.liquid)
 *     <div data-rail-tablist>                            optional tab container; `[data-rail-tab]` buttons rendered
 *                                                       anywhere else in the root (next to their panel) move into it on init
 *     <button ref="tabs[]" role="tab" aria-controls="PANEL_ID" aria-selected="true|false" tabindex="-1 on inactive"
 *             on:click="/selectTab" on:keydown="/onTabKeydown">        optional, one per panel
 *     <button ref="prev" on:click="/scrollPrevious">                   optional arrow
 *     <button ref="next" on:click="/scrollNext">                       optional arrow
 *     <div ref="panels[]" id="PANEL_ID" [hidden]>                      one per tab (or a single one, id optional)
 *       <div data-rail-track>                                          the horizontal scroller
 *         <div class="product-rail__item">...</div>                    items: get `--i` (0…6) for the stagger
 *       </div>
 *       <span data-rail-progress></span>                               optional: gets --progress-width/-offset
 *     </div>
 *   </product-rail>
 *
 * The first panel without `hidden` is active. Tab switches toggle `hidden`, replay `is-entering` on the
 * panel and reset the track. Arrows get `disabled` at the ends of the active track.
 *
 * Inserted later (recommendations): inserting the whole element just works. If you replace the children
 * of an already connected <product-rail>, call `rail.updatedCallback()` afterwards.
 */

/**
 * @typedef {Object} ProductRailRefs
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 */

/** @extends {Component<ProductRailRefs>} */
class ProductRail extends Component {
  /** @type {HTMLElement | null} */
  #active = null;

  connectedCallback() {
    super.connectedCallback();
    // Track scroll events do not bubble: catch them in the capture phase on the root
    this.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    // Theme editor: selecting a tab block (its panel) shows that panel
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('scroll', this.#onScroll, { capture: true });
    window.removeEventListener('resize', this.#onScroll);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  /**
   * Activates the clicked tab.
   * @param {Event} event
   */
  selectTab(event) {
    const tab = this.#tabFromEvent(event);
    if (tab) this.#select(tab, false);
  }

  /**
   * Arrow keys move between tabs.
   * @param {KeyboardEvent} event
   */
  onTabKeydown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const tabs = this.refs.tabs || [];
    const tab = this.#tabFromEvent(event);
    if (!tab) return;
    event.preventDefault();
    const index = tabs.indexOf(tab);
    const next = tabs[(index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
    if (next) this.#select(next, true);
  }

  scrollPrevious() {
    this.#step(-1);
  }

  scrollNext() {
    this.#step(1);
  }

  #init() {
    this.#mountTabs();
    const panels = this.refs.panels || [];
    this.#active = panels.find((panel) => !panel.hidden) || panels[0] || null;
    for (const panel of panels) {
      const track = this.#trackOf(panel);
      if (!track) continue;
      track.querySelectorAll('.product-rail__item').forEach((item, index) => {
        if (item instanceof HTMLElement) item.style.setProperty('--i', String(Math.min(index, 6)));
      });
    }
    this.#update();
  }

  /**
   * Tabs are rendered by each panel block (a section can't read its theme blocks' settings):
   * move them from beside their panels into the header tablist, in panel order.
   */
  #mountTabs() {
    const list = this.querySelector('[data-rail-tablist]');
    if (!list) return;
    for (const tab of this.querySelectorAll('[data-rail-tab]')) {
      if (tab.parentElement === list || tab.closest('product-rail') !== this) continue;
      // A re-rendered block brings a fresh copy of its tab: replace the stale one
      for (const old of list.querySelectorAll('[data-rail-tab]')) if (old.id === tab.id) old.remove();
      list.append(tab);
    }
  }

  /**
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #tabFromEvent(event) {
    const target = event.target instanceof Element ? event.target : null;
    return (this.refs.tabs || []).find((tab) => target && tab.contains(target)) || null;
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} focus
   */
  #select(tab, focus) {
    for (const item of this.refs.tabs || []) {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    }
    const id = tab.getAttribute('aria-controls');
    for (const panel of this.refs.panels || []) {
      const selected = panel.id === id;
      panel.hidden = !selected;
      if (!selected) continue;
      this.#active = panel;
      replayClass(panel, 'is-entering');
      const track = this.#trackOf(panel);
      if (track) track.scrollLeft = 0;
    }
    if (focus) tab.focus();
    this.#update();
  }

  /**
   * @param {Element | null} panel
   * @returns {HTMLElement | null}
   */
  #trackOf(panel) {
    const track = panel?.querySelector('[data-rail-track]');
    return track instanceof HTMLElement ? track : null;
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const panel = (this.refs.panels || []).find((item) => event.target instanceof Node && item.contains(event.target));
    const tab = panel && (this.refs.tabs || []).find((item) => item.getAttribute('aria-controls') === panel.id);
    if (tab) this.#select(tab, false);
  };

  #onScroll = () => {
    requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.#trackOf(this.#active);
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const ratio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
    const progress = max > 0 ? track.scrollLeft / max : 0;
    const bar = this.#active?.querySelector('[data-rail-progress]');
    if (bar instanceof HTMLElement) {
      bar.style.setProperty('--progress-width', `${Math.max(ratio * 100, 8)}%`);
      bar.style.setProperty('--progress-offset', `${progress * (1 / Math.max(ratio, 0.08) - 1) * 100}%`);
    }
    const { prev, next } = this.refs;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }

  /** @param {number} direction */
  #step(direction) {
    const track = this.#trackOf(this.#active);
    if (!track) return;
    const item = track.querySelector('.product-rail__item');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const per = item ? item.getBoundingClientRect().width + gap : track.clientWidth;
    const visible = Math.max(1, Math.floor((track.clientWidth + gap) / per));
    track.scrollBy({ left: direction * per * visible, behavior: scrollBehavior() });
  }
}

if (!customElements.get('product-rail')) {
  customElements.define('product-rail', ProductRail);
}
